import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured, DatabaseRoom, DatabaseResident, DatabasePaymentPeriod } from '../lib/supabase';
import { calculateNextDueDateAnchored } from '../utils/dates';
import { Room, Resident, PaymentPeriod } from '../types';
import { User } from '../types/auth';
import { logPaymentAdded, logResidentEdited } from '../utils/activityLogger';

export function useSupabaseRooms(currentUser?: User) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to determine resident position from username
  const getUserPosition = (username: string): 1 | 2 => {
    return username.endsWith('bb') ? 2 : 1;
  };

  // Helper function to check if user can access this resident's data
  const canUserAccessResident = (user: User | undefined, room: Room, resident: Resident): boolean => {
    if (!user) return false;
    
    // Admins and managers can access all data
    if (user.role === 'admin' || user.role === 'manager') return true;
    
    // Regular users can access all data
    if (user.role === 'user') return true;
    
    // Residents can only access their own room and position
    if (user.role === 'resident') {
      const userPosition = getUserPosition(user.username);
      return room.number === user.roomNumber && resident.position === userPosition;
    }
    
    return false;
  };

  // Filter rooms and residents based on user permissions
  const filterDataForUser = (rooms: Room[], user?: User): Room[] => {
    if (!user || user.role !== 'resident') {
      console.log('🔓 Non-resident user - showing all data');
      return rooms; // Non-residents see all data
    }

    // For residents, filter to only their room and position
    const userPosition = getUserPosition(user.username);
    console.log(`🔒 Resident user: ${user.username}, Room: ${user.roomNumber}, Position: ${userPosition}`);
    
    const filteredRooms = rooms
      .filter(room => room.number === user.roomNumber)
      .map(room => ({
        ...room,
        residents: room.residents.filter(resident => resident.position === userPosition)
      }));
      
    console.log(`📊 Filtered data: ${filteredRooms.length} rooms, ${filteredRooms.reduce((count, room) => count + room.residents.length, 0)} residents`);
    return filteredRooms;
  };

  // Convert database types to app types
  const convertToAppTypes = (
    dbRooms: DatabaseRoom[],
    dbResidents: DatabaseResident[],
    dbPaymentPeriods: DatabasePaymentPeriod[]
  ): Room[] => {
    return dbRooms.map(dbRoom => {
      const roomResidents = dbResidents
        .filter(resident => resident.room_id === dbRoom.id)
        .map(dbResident => {
          const residentPaymentPeriods = dbPaymentPeriods
            .filter(period => period.resident_id === dbResident.id)
            .sort((a, b) => a.period_number - b.period_number)
            .map(dbPeriod => ({
              id: dbPeriod.id,
              periodNumber: dbPeriod.period_number,
              dpDate: dbPeriod.dp_date || '',
              startDate: dbPeriod.start_date || '',
              paymentProofDate: dbPeriod.payment_proof_date || '',
              paymentProofLink: dbPeriod.payment_proof_link || '',
              nextPaymentDate: dbPeriod.next_payment_date || '',
              category: (dbPeriod as any).category || undefined,
              installmentNumber: (dbPeriod as any).installment_number || undefined
            } as PaymentPeriod));

          return {
            id: dbResident.id,
            name: dbResident.name,
            phone: dbResident.phone,
            study: dbResident.study,
            position: dbResident.position,
            paymentPattern: dbResident.payment_pattern,
            startDate: dbResident.start_date || '',
            nextPaymentDate: dbResident.next_payment_date || '',
            motorcyclePlate: dbResident.motorcycle_plate || '',
            paymentPeriods: residentPaymentPeriods,
            roomNumber: dbRoom.number
          } as Resident;
        })
        .sort((a, b) => a.position - b.position);

      return {
        number: dbRoom.number,
        residents: roomResidents,
        isExpanded: dbRoom.is_expanded,
        buildingType: (dbRoom.building_type as 'gajah4' | 'gajah3') || 'gajah4'
      } as Room;
    });
  };

  // Load data from Supabase
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isSupabaseConfigured) {
        throw new Error('Konfigurasi Supabase tidak ditemukan. Set VITE_SUPABASE_URL dan VITE_SUPABASE_ANON_KEY di Environment Variables hosting (Vercel).');
      }
      
      console.log('Loading data from Supabase...');

      // Fetch all data with proper ordering
      const [roomsResult, residentsResult, paymentPeriodsResult] = await Promise.all([
        supabase
          .from('rooms')
          .select('*')
          .order('number'),
        supabase
          .from('residents')
          .select('*')
          .order('position'),
        supabase
          .from('payment_periods')
          .select('*')
          .order('period_number')
      ]);

      if (roomsResult.error) throw roomsResult.error;
      if (residentsResult.error) throw residentsResult.error;
      if (paymentPeriodsResult.error) throw paymentPeriodsResult.error;
      
      console.log('Data fetched successfully:', {
        rooms: roomsResult.data?.length || 0,
        residents: residentsResult.data?.length || 0,
        paymentPeriods: paymentPeriodsResult.data?.length || 0
      });

  let convertedRooms = convertToAppTypes(
        roomsResult.data || [],
        residentsResult.data || [],
        paymentPeriodsResult.data || []
      );

  // Ensure all rooms collapsed by default on initial load
  convertedRooms = convertedRooms.map(r => ({ ...r, isExpanded: false }));

      // Sort rooms by custom order: 1-10, then 101-199, then 200-299, then 300+
      const sortedRooms = convertedRooms.sort((a, b) => {
        const numA = parseInt(a.number);
        const numB = parseInt(b.number);
        
        // Define floor priorities
        const getFloorPriority = (num: number) => {
          if (num >= 1 && num <= 10) return 1;      // Ground floor: 1-10
          if (num >= 101 && num <= 199) return 2;   // First floor: 101-199
          if (num >= 200 && num <= 299) return 3;   // Second floor: 200-299
          if (num >= 300) return 4;                 // Third floor and above: 300+
          return 5; // Any other numbers go last
        };
        
        const floorA = getFloorPriority(numA);
        const floorB = getFloorPriority(numB);
        
        // First sort by floor priority
        if (floorA !== floorB) {
          return floorA - floorB;
        }
        
        // Then sort by room number within the same floor
        return numA - numB;
      });
      
      // Apply user-specific filtering
      const filteredRooms = filterDataForUser(sortedRooms, currentUser);
      setRooms(filteredRooms);
    } catch (err) {
      console.error('Error loading data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to load data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on mount and when currentUser changes
  useEffect(() => {
    loadData();
  }, [currentUser]); // Add currentUser as dependency

  // Add new payment period
  const addPaymentPeriod = async (
    roomNumber: string,
    residentPosition: 1 | 2,
    periodData: {
      periodNumber: number;
      paymentProofDate?: string;
      paymentProofLink?: string;
      nextPaymentDate?: string;
  category?: 'DP' | 'Pelunasan' | 'Cicilan';
  installmentNumber?: number;
    }
  ) => {
    try {
      // Find the room
      const room = rooms.find(r => r.number === roomNumber);
      if (!room) throw new Error('Room not found');

      // Find the resident
      const resident = room.residents.find(r => r.position === residentPosition);
      if (!resident) throw new Error('Resident not found');

      // Check if current user has permission to add payment for this resident
      if (currentUser && !canUserAccessResident(currentUser, room, resident)) {
        throw new Error('Anda tidak memiliki akses untuk menambah pembayaran penghuni ini');
      }

      // Compute next payment date if not supplied, anchored to resident start_date and after payment proof date
      let computedNext = periodData.nextPaymentDate;
      // Cicilan ke-2+: keep existing period due so sisa payment does not shift next rent due
      if (
        periodData.category === 'Cicilan' &&
        Number(periodData.installmentNumber || 0) >= 2 &&
        resident.nextPaymentDate
      ) {
        computedNext = periodData.nextPaymentDate || resident.nextPaymentDate;
      } else if (!computedNext) {
        const start = resident.startDate || new Date().toISOString().slice(0,10);
        const pattern = resident.paymentPattern || 1;
        // For Pelunasan or Cicilan, we still compute the next due strictly after payment_proof_date
        const after = periodData.paymentProofDate || resident.nextPaymentDate || start;
        computedNext = calculateNextDueDateAnchored(start, pattern, after);
      }

      // Insert payment period
      const { error } = await supabase
        .from('payment_periods')
        .insert({
          resident_id: resident.id,
          period_number: periodData.periodNumber,
          dp_date: null,
          start_date: null,
          payment_proof_date: periodData.paymentProofDate || null,
          payment_proof_link: periodData.paymentProofLink || '',
          next_payment_date: computedNext || null,
          category: periodData.category || null,
          installment_number: periodData.installmentNumber || null
        });

      if (error) throw error;

      // Update resident's next payment date
    if (computedNext) {
        await supabase
          .from('residents')
      .update({ next_payment_date: computedNext })
          .eq('id', resident.id);
      }

      // Log activity for notification
      await logPaymentAdded(
        roomNumber,
        resident.name,
        undefined,
        periodData.category
      );

      // Reload data to reflect changes
      await loadData();
      return true;
    } catch (err) {
      console.error('Error adding payment period:', err);
      setError(err instanceof Error ? err.message : 'Failed to add payment period');
      return false;
    }
  };

  // Add new resident
  const addResident = async (
    roomNumber: string,
    position: 1 | 2,
    residentData: {
      name: string;
      phone: string;
      motorcyclePlate?: string;
      study: string;
      startDate: string;
      paymentPattern: number;
      paymentProofDate?: string;
      paymentProofLink?: string;
      nextPaymentDate?: string;
      category?: 'DP' | 'Pelunasan' | 'Cicilan';
      installmentNumber?: number;
    }
  ) => {
    try {
      // Check if current user has permission to add resident to this position
      if (currentUser && currentUser.role === 'resident') {
        const userPosition = getUserPosition(currentUser.username);
        if (roomNumber !== currentUser.roomNumber || position !== userPosition) {
          throw new Error('Anda hanya dapat menambah data penghuni untuk kamar dan posisi Anda sendiri');
        }
      }
      // Find or create the room
  let { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('id')
        .eq('number', roomNumber)
        .single();

      if (roomError && roomError.code === 'PGRST116') {
        // Room doesn't exist, create it
        const { data: newRoom, error: createRoomError } = await supabase
          .from('rooms')
          .insert({ number: roomNumber, is_expanded: false })
          .select('id')
          .single();

        if (createRoomError) throw createRoomError;
        roomData = newRoom;
      } else if (roomError) {
        throw roomError;
      }

      if (!roomData) {
        throw new Error('Room lookup failed.');
      }

      // Use provided next payment date or calculate if not provided
      let nextPaymentDate = residentData.nextPaymentDate;
      if (!nextPaymentDate) {
        // Fallback calculation if no date provided
        const startDate = new Date(residentData.startDate);
        const nextDate = new Date(startDate);
        nextDate.setMonth(nextDate.getMonth() + residentData.paymentPattern);
        nextDate.setHours(0, 0, 0, 0);
        nextPaymentDate = nextDate.toISOString().split('T')[0];
      }

      // Insert resident
      const { data: newResident, error: residentError } = await supabase
        .from('residents')
        .insert({
          room_id: roomData.id,
          name: residentData.name,
          phone: residentData.phone,
          motorcycle_plate: residentData.motorcyclePlate || '',
          study: residentData.study,
          position,
          payment_pattern: residentData.paymentPattern,
          start_date: residentData.startDate,
          next_payment_date: nextPaymentDate
        })
        .select('id')
        .single();

      if (residentError) throw residentError;

  // If payment information is provided, create initial payment period (period 1)
      if (residentData.paymentProofDate || residentData.paymentProofLink) {
        await supabase
          .from('payment_periods')
          .insert({
            resident_id: newResident.id,
    period_number: 1,
            dp_date: null,
            start_date: residentData.startDate,
            payment_proof_date: residentData.paymentProofDate || null,
            payment_proof_link: residentData.paymentProofLink || '',
            next_payment_date: nextPaymentDate,
            category: residentData.category || null,
            installment_number: residentData.category === 'Cicilan' ? (residentData.installmentNumber || 1) : null
          });
      }

      // Reload data to reflect changes
      await loadData();
      return true;
    } catch (err) {
      console.error('Error adding resident:', err);
      setError(err instanceof Error ? err.message : 'Failed to add resident');
      return false;
    }
  };

  // Update resident
  const updateResident = async (residentId: string, updates: Partial<Resident>) => {
    try {
      // Get resident info before updating for logging
      const residentBefore = rooms.flatMap(r => r.residents).find(r => r.id === residentId);
      
      // Prepare the update object with database field names
      const dbUpdates: any = {};
      
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.motorcyclePlate !== undefined) dbUpdates.motorcycle_plate = updates.motorcyclePlate;
      if (updates.study !== undefined) dbUpdates.study = updates.study;
      if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
      if (updates.paymentPattern !== undefined) dbUpdates.payment_pattern = updates.paymentPattern;
      if (updates.nextPaymentDate !== undefined) dbUpdates.next_payment_date = updates.nextPaymentDate;
      if (updates.motorcyclePlate !== undefined) dbUpdates.motorcycle_plate = updates.motorcyclePlate;
      
      // If start date or payment pattern changed, recalculate next payment date
      if (updates.startDate || updates.paymentPattern) {
        const resident = rooms.flatMap(r => r.residents).find(r => r.id === residentId);
        if (resident) {
          const startDate = new Date(updates.startDate || resident.startDate);
          const pattern = updates.paymentPattern || resident.paymentPattern;
          
          // Calculate next payment date based on latest payment period or start date
          let nextDate = new Date(startDate);
          if (resident.paymentPeriods && resident.paymentPeriods.length > 0) {
            const latestPeriod = resident.paymentPeriods[resident.paymentPeriods.length - 1];
            nextDate.setMonth(nextDate.getMonth() + ((latestPeriod.periodNumber + 1) * pattern));
          } else {
            nextDate.setMonth(nextDate.getMonth() + pattern);
          }
          nextDate.setHours(0, 0, 0, 0);
          dbUpdates.next_payment_date = nextDate.toISOString().split('T')[0];
        }
      }

      const { error } = await supabase
        .from('residents')
        .update(dbUpdates)
        .eq('id', residentId);

      if (error) throw error;

      // Log the update
      if (residentBefore) {
        const changes: string[] = [];
        if (updates.name) changes.push('nama');
        if (updates.phone) changes.push('nomor HP');
        if (updates.study) changes.push('asal kuliah');
        if (updates.startDate) changes.push('tanggal masuk');
        if (updates.paymentPattern) changes.push('pola bayar');
        if (updates.motorcyclePlate) changes.push('plat motor');
        
        await logResidentEdited(
          residentBefore.roomNumber || '',
          updates.name || residentBefore.name || 'Penghuni',
          changes
        );
      }

      // Reload data to reflect changes
      await loadData();
      return true;
    } catch (err) {
      console.error('Error updating resident:', err);
      setError(err instanceof Error ? err.message : 'Failed to update resident');
      return false;
    }
  };

  // Delete payment period
  const deletePaymentPeriod = async (paymentPeriodId: string) => {
    try {
      // Get payment period info before deleting for logging
      const { data: periodData } = await supabase
        .from('payment_periods')
        .select('*, residents(name, roomNumber)')
        .eq('id', paymentPeriodId)
        .single();

      const { error } = await supabase
        .from('payment_periods')
        .delete()
        .eq('id', paymentPeriodId);

      if (error) throw error;

      // Log the deletion
      if (periodData) {
        const resident = periodData.residents as any;
        const { logPaymentDeleted } = await import('../utils/activityLogger');
        await logPaymentDeleted(
          resident?.roomNumber || '',
          resident?.name || 'Penghuni'
        );
      }

      // Reload data to reflect changes
      await loadData();
      return true;
    } catch (err) {
      console.error('Error deleting payment period:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete payment period');
      return false;
    }
  };

  // Update payment period (edit pembayaran)
  const updatePaymentPeriod = async (
    resident: Resident,
    period: PaymentPeriod,
    updates: {
      paymentProofDate?: string;
      paymentProofLink?: string;
      category?: 'DP' | 'Pelunasan' | 'Cicilan';
      installmentNumber?: number;
      periodNumber?: number;
    }
  ) => {
    try {
      // Compute next payment date anchored to resident start date and strictly after updated proof date
      const after = updates.paymentProofDate || period.paymentProofDate || resident.nextPaymentDate || resident.startDate;
      const computedNext = calculateNextDueDateAnchored(resident.startDate, resident.paymentPattern || 1, after);

      // Build update object and include period_number only when provided
      const updateObj: any = {
        payment_proof_date: updates.paymentProofDate ?? period.paymentProofDate ?? null,
        payment_proof_link: updates.paymentProofLink ?? period.paymentProofLink ?? '',
        category: updates.category ?? (period as any).category ?? null,
        installment_number: updates.category === 'Cicilan' ? (updates.installmentNumber ?? (period as any).installmentNumber ?? 1) : null,
        next_payment_date: computedNext || null
      };
      if (updates.periodNumber !== undefined && updates.periodNumber !== null) {
        updateObj.period_number = updates.periodNumber;
      }

      const { error } = await supabase
        .from('payment_periods')
        .update(updateObj)
        .eq('id', period.id);

      if (error) throw error;

      // Update resident's next payment date to latest based on their payment periods
      const { data: periods, error: fetchErr } = await supabase
        .from('payment_periods')
        .select('id, period_number, next_payment_date')
        .eq('resident_id', resident.id);
      if (fetchErr) throw fetchErr;
      let latestNext: string | null = null;
      let maxPeriod = -1;
      (periods || []).forEach(p => {
        if (p.period_number > maxPeriod && p.next_payment_date) {
          maxPeriod = p.period_number;
          latestNext = p.next_payment_date as unknown as string;
        }
      });
      if (latestNext) {
        await supabase
          .from('residents')
          .update({ next_payment_date: latestNext })
          .eq('id', resident.id);
      }

      await loadData();
      return true;
    } catch (err) {
      console.error('Error updating payment period:', err);
      setError(err instanceof Error ? err.message : 'Failed to update payment period');
      return false;
    }
  };

  // Manual backup resident (without deleting)
  const backupResident = async (residentId: string, reason?: string) => {
    try {
      // Call the database function to backup resident
      const { data, error } = await supabase.rpc('manual_backup_resident', {
        p_resident_id: residentId,
        p_backup_reason: reason || 'Manual backup'
      });

      if (error) throw error;

      // Check if backup was successful
      if (data && data.success) {
        console.log('✅ Backup successful:', data);
        return { success: true, data };
      } else {
        throw new Error(data?.message || 'Backup failed');
      }
    } catch (err) {
      console.error('Error backing up resident:', err);
      setError(err instanceof Error ? err.message : 'Failed to backup resident');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to backup resident' };
    }
  };

  // Delete resident
  const deleteResident = async (residentId: string) => {
    try {
      const { error } = await supabase
        .from('residents')
        .delete()
        .eq('id', residentId);

      if (error) throw error;

      // Reload data to reflect changes
      await loadData();
      return true;
    } catch (err) {
      console.error('Error deleting resident:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete resident');
      return false;
    }
  };

  // Move resident
  const moveResident = async (
    fromRoomNumber: string,
    fromPosition: 1 | 2,
    toRoomNumber: string,
    toPosition: 1 | 2
  ) => {
    try {
      // Find the source resident
      const fromRoom = rooms.find(r => r.number === fromRoomNumber);
      if (!fromRoom) throw new Error('Source room not found');

      const resident = fromRoom.residents.find(r => r.position === fromPosition);
      if (!resident) throw new Error('Source resident not found');

      // Find or create the destination room
  let { data: toRoomData, error: toRoomError } = await supabase
        .from('rooms')
        .select('id')
        .eq('number', toRoomNumber)
        .single();

      if (toRoomError && toRoomError.code === 'PGRST116') {
        // Room doesn't exist, create it
        const { data: newRoom, error: createRoomError } = await supabase
          .from('rooms')
          .insert({ number: toRoomNumber, is_expanded: false })
          .select('id')
          .single();

        if (createRoomError) throw createRoomError;
        toRoomData = newRoom;
      } else if (toRoomError) {
        throw toRoomError;
      }

      if (!toRoomData) {
        throw new Error('Destination room lookup failed.');
      }

      // Update the resident's room and position
      const { error: updateError } = await supabase
        .from('residents')
        .update({
          room_id: toRoomData.id,
          position: toPosition
        })
        .eq('id', resident.id);

      if (updateError) throw updateError;

      // Reload data to reflect changes
      await loadData();
      return true;
    } catch (err) {
      console.error('Error moving resident:', err);
      setError(err instanceof Error ? err.message : 'Failed to move resident');
      return false;
    }
  };

  // Toggle room expansion
  const toggleRoomExpansion = async (roomNumber: string) => {
    try {
      const room = rooms.find(r => r.number === roomNumber);
      if (!room) return;

      const { error } = await supabase
        .from('rooms')
        .update({ is_expanded: !room.isExpanded })
        .eq('number', roomNumber);

      if (error) throw error;

      // Update local state immediately for better UX
      setRooms(prevRooms =>
        prevRooms.map(r =>
          r.number === roomNumber
            ? { ...r, isExpanded: !room.isExpanded }
            : r
        )
      );
    } catch (err) {
      console.error('Error toggling room expansion:', err);
      setError(err instanceof Error ? err.message : 'Failed to toggle room');
    }
  };

  // Get late payments
  const getLatePayments = () => {
    // Gunakan tanggal 15 sebagai patokan untuk pembayaran tertunggak
    console.log('🔍 Checking late payments with current date:', new Date().toISOString());
    
  const today = new Date();
    today.setHours(0, 0, 0, 0); // Set ke awal hari untuk perbandingan yang konsisten
    
    const latePayments: Resident[] = [];
    let checkedResidents = 0;
    let foundLatePayments = 0;
    
    rooms.forEach(room => {
      room.residents.forEach(resident => {
        if (!resident?.name || !resident.name.trim()) return; // Skip empty residents
        
        // Tentukan tanggal jatuh tempo yang benar
        let correctDueDate = '';
        
        // Jika penghuni memiliki periode pembayaran, gunakan nextPaymentDate dari periode terakhir
        if (resident.paymentPeriods && resident.paymentPeriods.length > 0) {
          // Cari periode pembayaran terakhir
          let latestPeriod = resident.paymentPeriods[0];
          resident.paymentPeriods.forEach(period => {
            if (period.periodNumber > latestPeriod.periodNumber) {
              latestPeriod = period;
            }
          });
          
          // Skip jika kategori terakhir adalah DP (DP tidak masuk daftar tertunggak)
          if (latestPeriod.category === 'DP') {
            console.log(`⏭️ Skipping DP payment: ${resident.name} (Room ${resident.roomNumber}) - Category: ${latestPeriod.category}`);
            return;
          }
          
          // Gunakan nextPaymentDate dari periode terakhir sebagai tanggal yang benar
          if (latestPeriod.nextPaymentDate && latestPeriod.nextPaymentDate.trim()) {
            correctDueDate = latestPeriod.nextPaymentDate;
          }
        } else {
          // Jika tidak ada periode pembayaran, gunakan nextPaymentDate dari resident
          correctDueDate = resident.nextPaymentDate;
        }
        
        // Skip jika tidak ada tanggal jatuh tempo yang valid
        if (!correctDueDate) return;
        
        checkedResidents++;
        
        // Cek apakah pembayaran sudah lewat jatuh tempo (tanggal jatuh tempo < hari ini)
        const dueDate = new Date(correctDueDate);
        dueDate.setHours(0, 0, 0, 0);
        
        console.log(`📅 Checking ${resident.name} (Room ${resident.roomNumber}): Due ${correctDueDate}, Today ${today.toISOString().split('T')[0]}`);
        
        // Pembayaran dianggap tertunggak jika tanggal jatuh tempo sudah lewat (< hari ini)
        if (dueDate < today) {
          // Update nextPaymentDate resident untuk konsistensi
          resident.nextPaymentDate = correctDueDate;
          latePayments.push(resident);
          foundLatePayments++;
          console.log(`📅 Late payment found: ${resident.name} (Room ${resident.roomNumber}) - Due: ${correctDueDate}`);
        } else if (dueDate.getTime() === today.getTime()) {
          console.log(`⏰ Payment due today: ${resident.name} (Room ${resident.roomNumber}) - Due: ${correctDueDate}`);
        } else {
          console.log(`✅ Payment not due yet: ${resident.name} (Room ${resident.roomNumber}) - Due: ${correctDueDate}`);
        }
      });
    });

    console.log(`✅ Late payment check completed:`);
    console.log(`   📊 Residents checked: ${checkedResidents}`);
    console.log(`   🚨 Late payments found: ${foundLatePayments}`);
    
    return latePayments;
  };

  // Get residents who have never uploaded payment proof (excluding DP payments)
  const getResidentsWithoutPaymentProof = () => {
    console.log('🔍 Checking residents without payment proof (excluding DP)');
    
    const residentsWithoutProof: Resident[] = [];
    let checkedResidents = 0;
    
    rooms.forEach(room => {
      room.residents.forEach(resident => {
        if (!resident?.name || !resident.name.trim()) return; // Skip empty residents
        
        checkedResidents++;
        
        // Check if resident has any NON-DP payment periods with proof
        const hasRegularPaymentProof = resident.paymentPeriods && resident.paymentPeriods.some(period => 
          // Exclude DP category from proof check
          period.category !== 'DP' && (
            period.paymentProofDate && period.paymentProofDate.trim() !== '' ||
            period.paymentProofLink && period.paymentProofLink.trim() !== ''
          )
        );
        
        // Also check if resident has any regular payment periods at all (Pelunasan or Cicilan)
        const hasRegularPaymentPeriods = resident.paymentPeriods && resident.paymentPeriods.some(period => 
          period.category === 'Pelunasan' || period.category === 'Cicilan'
        );
        
        // Only count as "without proof" if they have regular payment periods but no proof for them
        if (hasRegularPaymentPeriods && !hasRegularPaymentProof) {
          console.log(`📝 No regular payment proof found: ${resident.name} (Room ${resident.roomNumber}, Position ${resident.position})`);
          residentsWithoutProof.push(resident);
        } else if (!hasRegularPaymentPeriods) {
          console.log(`⏭️ Skipping ${resident.name} - only has DP payments or no payments yet`);
        } else {
          console.log(`✅ Regular payment proof found: ${resident.name} (Room ${resident.roomNumber}, Position ${resident.position})`);
        }
      });
    });

    console.log(`✅ Payment proof check completed:`);
    console.log(`   📊 Residents checked: ${checkedResidents}`);
    console.log(`   📝 Without regular payment proof: ${residentsWithoutProof.length}`);
    
    return residentsWithoutProof;
  };

  return {
    rooms,
    loading,
    error,
    addResident,
    addPaymentPeriod,
  updatePaymentPeriod,
    updateResident,
    moveResident,
    deletePaymentPeriod,
    deleteResident,
    backupResident,
    toggleRoomExpansion,
    getLatePayments,
    getResidentsWithoutPaymentProof,
    refreshData: loadData
  };
}
